import { FC, useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { marked } from 'marked';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    alpha,
} from '@mui/material';
import { AutoAwesome, Close, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CampaignManagementQuery } from '../../../../services/query';

interface SCUIAISuggestionDialogProps {
    open: boolean;
    campaignId: number | null;
    onClose: () => void;
}

export const SCUIAISuggestionDialog: FC<SCUIAISuggestionDialogProps> = ({ open, campaignId, onClose }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState('');

    const fetchSuggestion = async () => {
        if (!campaignId) return;

        setLoading(true);
        try {
            const response = await CampaignManagementQuery.GetAISuggestion({ campaignRule: campaignId });
            if (response?.content) {
                setContent(response?.content);
            } else {
                setContent('');
            }
        } catch (error) {
            console.error('Error fetching AI suggestion:', error);
            setContent('');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && campaignId) {
            fetchSuggestion();
        }
        if (!open) {
            setContent('');
        }
    }, [open, campaignId]);

    const renderedHtml = content ? DOMPurify.sanitize(marked.parse(content) as string) : '';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        minHeight: 560,
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <AutoAwesome sx={{ color: 'primary.main', fontSize: 22 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>AI Suggestion</Typography>
                    {campaignId && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                            #{campaignId}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        onClick={fetchSuggestion}
                        disabled={loading}
                        size="small"
                        startIcon={<Refresh sx={{ fontSize: 18 }} />}
                        sx={{
                            color: 'success.main',
                            bgcolor: (theme) =>
                                alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                            border: '1px solid',
                            borderColor: (theme) =>
                                alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.25),
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            width: '120px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: (theme) =>
                                    alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.26 : 0.16),
                            },
                            '&:disabled': {
                                color: 'text.disabled',
                            },
                        }}
                    >
                        Refresh
                    </Button>
                    <Button onClick={onClose} size="small" sx={{ color: 'text.secondary', minWidth: 0, px: 1 }}>
                        <Close sx={{ fontSize: 18 }} />
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 220,
                            gap: 2,
                        }}
                    >
                        <CircularProgress size={36} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('general.loading')}
                        </Typography>
                    </Box>
                ) : renderedHtml ? (
                    <Box
                        sx={{
                            padding: '16px 24px',
                            '& h1': { fontSize: '1.3rem', fontWeight: 700, mt: 0, mb: 1.5 },
                            '& h2': { fontSize: '1.15rem', fontWeight: 700, mt: 2, mb: 1 },
                            '& h3': { fontSize: '1rem', fontWeight: 600, mt: 2, mb: 1 },
                            '& p': { fontSize: '0.9rem', lineHeight: 1.7, mb: 1 },
                            '& ul, & ol': { pl: 2.5, mb: 1.5 },
                            '& li': { fontSize: '0.9rem', lineHeight: 1.7, mb: 0.5 },
                            '& strong': { fontWeight: 600 },
                            '& hr': { my: 2, borderColor: 'divider' },
                            '& blockquote': {
                                borderLeft: '4px solid',
                                borderColor: 'warning.main',
                                pl: 2,
                                py: 0.5,
                                my: 1.5,
                                ml: 0,
                                bgcolor: (theme) =>
                                    alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.08 : 0.05),
                                borderRadius: '0 8px 8px 0',
                            },
                        }}
                    >
                        {parse(renderedHtml)}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 220,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('general.notFound')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                    }}
                >
                    {t('general.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
